// Planner V2 side of the FC spread target (spec docs/superpowers/specs/2026-10-28-fc-spread-target-design.md §7).

const missingText = (live) => Object.entries(live?.missingByRegion || {})
  .filter(([, n]) => n > 0).map(([r, n]) => `${r} ×${n}`).join(', ');

export function spreadLineText(item) {
  if (!item?.spreadQty) return null;
  const live = item.spreadLive;
  if (!live || live.status === 'UNKNOWN') return `${item.spreadQty} spread units · spread unknown now`;
  if (live.status === 'AT_TARGET' || !live.missing) return `${item.spreadQty} spread units · now at target — ship as depth`;
  return `${item.spreadQty} spread units · in ${live.heldFcs} FCs → missing ${missingText(live)}`;
}

// Final review F5: spread = min(what the PO set aside, what is still to ship, what the live spread still needs).
const liveCap = (live) => (Number.isFinite(Number(live?.spreadUnits)) && live?.spreadUnits != null
  ? Math.max(0, Number(live.spreadUnits)) : Infinity);

export function splitPoLine(item, shipQty) {
  const qty = Number(shipQty) || 0;
  const live = item?.spreadLive;
  if (!item?.spreadQty || !live || live.status !== 'OK' || !live.missing) return { depthQty: qty, spreadQty: 0 };
  const spreadQty = Math.min(item.spreadQty, qty, liveCap(live));
  return { depthQty: qty - spreadQty, spreadQty };
}

/**
 * After a confirm (F5): the line's spread re-derived from the reloaded live spread, so it drops once regions
 * fill. It never grows; UNKNOWN keeps the quantities (unknown is not zero). PO / product lines keep their
 * total (released spread units ship as depth); a refill line's units exist only for the regions, so they drop.
 */
export function rederiveSpread(line, view) {
  if (!view || view.status === 'UNKNOWN') return { spreadLive: view, depthQty: line.depthQty, spreadQty: line.spreadQty };
  if (line.source === 'Refill regions') {
    const item = refillItem(line.asin, view);
    return { spreadLive: item ? item.spreadLive : view, depthQty: line.depthQty, spreadQty: item ? Math.min(line.spreadQty, item.spreadQty) : 0 };
  }
  const spreadQty = view.status === 'OK' ? Math.min(line.spreadQty, liveCap(view)) : 0;
  return { spreadLive: view, depthQty: line.depthQty + (line.spreadQty - spreadQty), spreadQty };
}

// Mirrors ShipmentWatchService.spreadChunkSpec: few, large, 5+ box chunks; null = one plan.
export function spreadChunkSpec(qty, unitsPerBox, missing) {
  const boxes = Math.ceil(qty / unitsPerBox);
  const k = Math.max(1, Math.min(Math.ceil(missing / 5), Math.floor(boxes / 5)));
  if (k < 2) return null;
  return `${k}x${Math.ceil(boxes / k) * unitsPerBox}`;
}

export function spreadWatchRequest(item, spreadQty, unitsPerBox, source) {
  return {
    items: [{ asin: item.asin, qty: spreadQty }],
    source,
    kickoff: true,
    drawIntervalHours: 12,
    chunkSpec: spreadChunkSpec(spreadQty, unitsPerBox, item.spreadLive.missing),
    purpose: 'SPREAD',
    spreadTarget: { missingByRegion: item.spreadLive.missingByRegion },
  };
}

export function refillItem(asin, view) {
  if (!view || view.status !== 'OK' || !view.spreadUnits) return null;
  const needRegions = {};
  Object.entries(view.regionNeed || {}).forEach(([r, n]) => {
    if (n > 0) needRegions[r] = Math.max(view.missingByRegion?.[r] || 0, 1);
  });
  const slots = Object.values(needRegions).reduce((a, b) => a + b, 0);
  return { asin, spreadQty: view.spreadUnits, spreadLive: { ...view, missing: slots, missingByRegion: needRegions } };
}

export function boxReady(pkg) {
  if (!pkg || !(pkg.unitsPerBox > 0)) return false;
  if (!pkg.boxLengthIn || !pkg.boxWidthIn || !pkg.boxHeightIn) return false;
  return !!(pkg.boxWeightLbs || pkg.unitWeightLbs);
}
